import { useAppSelector } from '@/app/redux/hooks';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { selectShareLink } from '@/app/redux/slices/shareLinkSlice';
import { StudentEvent, StudentEventType } from '@/common/types/studentEvents';
import { DataLoader } from '@/common/ui/LoadingSpinners';
import { monthNames } from '@/common/utils/datetime';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import SessionItem from '@/dashboard/LearnerView/ActivityFeed/SessionItem';
import SettingChangeItem from '@/dashboard/LearnerView/ActivityFeed/SettingChangeItem';
import api from '@/db/apiClient';
import { PulseIcon } from '@phosphor-icons/react';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@shadcn/components/accordion';
import { Separator } from '@shadcn/components/separator';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

interface ActivityFeedProps {
  studentId: string;
  studentName: string;
}
export default function ActivityFeed({
  studentId,
  studentName,
}: ActivityFeedProps) {
  const { currentOrganisationId } = useCurrentOrganisationId();

  const userId = useAppSelector(selectUserId);
  const { student } = useAppSelector(selectShareLink);

  const organisationId = useMemo(
    () => student?.organisationId ?? currentOrganisationId,
    [student, currentOrganisationId],
  );

  const [isLoading, setLoading] = useState(true);
  const [openMonths, setOpenMonths] = useState<string[]>(monthNames);
  const [groupedEvents, setGroupedEvents] = useState<
    // { year: { month: [events] } }
    Record<string, Record<string, StudentEvent[]>>
  >({});

  useEffect(() => {
    let unsubscribe = () => {};
    (async () => {
      if (!organisationId) return;
      setLoading(true);
      const service = api.asShareLink ?? api.asEducator;
      unsubscribe = await service.subscribeToEventsForStudent(
        organisationId,
        studentId,
        (events) => {
          const grouped = groupEventsByDate(events);
          setGroupedEvents(grouped);
          setLoading(false);

          // Track that feed was viewed
          const sessionCount = Object.values(grouped).reduce((acc, month) => {
            return (
              acc +
              Object.values(month).reduce((monthAcc, sessions) => {
                return (
                  monthAcc +
                  sessions.filter(
                    (e) => e.type === StudentEventType.SESSION_STARTED,
                  ).length
                );
              }, 0)
            );
          }, 0);

          const totalEvents = Object.values(grouped).reduce((acc, month) => {
            return (
              acc +
              Object.values(month).reduce(
                (monthAcc, sessions) => monthAcc + sessions.length,
                0,
              )
            );
          }, 0);

          trackMixpanelEvent('Activity Feed Viewed', {
            studentId,
            activityCount: totalEvents,
            sessionCount,
          });
        },
      );
    })();
    return () => unsubscribe();
  }, [studentId, userId, organisationId]);

  const eventCount = Object.values(groupedEvents).reduce((acc, month) => {
    return (
      acc +
      Object.values(month).reduce((acc, sessions) => {
        return acc + sessions.length;
      }, 0)
    );
  }, 0);

  function groupEventsByDate(events: StudentEvent[]) {
    const groupedEvents: Record<string, Record<string, StudentEvent[]>> = {};

    for (const event of events) {
      const date = dayjs(event.createdAt);
      const year = date.year();
      const month = monthNames[date.month()];
      if (!groupedEvents[year]) {
        groupedEvents[year] = {};
      }
      if (!groupedEvents[year][month]) {
        groupedEvents[year][month] = [];
      }
      groupedEvents[year][month].push(event);
    }

    return groupedEvents;
  }

  function handleAccordionChange(newOpenMonths: string[]) {
    // Track which months were added or removed
    const added = newOpenMonths.filter((m) => !openMonths.includes(m));
    const removed = openMonths.filter((m) => !newOpenMonths.includes(m));

    added.forEach((month) => {
      // Find year for this month
      let year = '';
      let activityCount = 0;
      for (const [y, months] of Object.entries(groupedEvents)) {
        if (months[month]) {
          year = y;
          activityCount = months[month].length;
          break;
        }
      }

      trackMixpanelEvent('Activity Month Expanded', {
        studentId,
        year,
        month,
        activityCount,
      });
    });

    removed.forEach((month) => {
      // Find year for this month
      let year = '';
      for (const [y, months] of Object.entries(groupedEvents)) {
        if (months[month]) {
          year = y;
          break;
        }
      }

      trackMixpanelEvent('Activity Month Collapsed', {
        studentId,
        year,
        month,
      });
    });

    setOpenMonths(newOpenMonths);
  }

  function renderEvent(event: StudentEvent) {
    switch (event.type) {
      case StudentEventType.SESSION_STARTED:
        return (
          <SessionItem
            key={event.id}
            sessionId={event.sessionId}
            studentId={studentId}
            studentName={studentName}
            organisationId={organisationId ?? undefined}
            renderEvent={renderEvent}
          />
        );
      case StudentEventType.SETTING_CHANGED:
        return (
          <SettingChangeItem
            key={event.id}
            studentName={studentName}
            event={event}
          />
        );
    }
  }

  // todo: use a skeleton
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <DataLoader />
      </div>
    );
  }

  return (
    <Accordion
      type="multiple"
      value={openMonths}
      onValueChange={handleAccordionChange}
      className="p-4"
    >
      {eventCount > 0 ? (
        Object.entries(groupedEvents)
          .sort((a, b) => Number(b[0]) - Number(a[0]))
          .map(([year, months]) => (
            <div key={year}>
              <YearTitle className="text-neutral-400">{year}</YearTitle>
              {Object.entries(months)
                .sort((a, b) => {
                  const monthA = monthNames.indexOf(a[0]);
                  const monthB = monthNames.indexOf(b[0]);
                  return monthB - monthA;
                })
                .map(([month, sessionsForMonth]) => (
                  <AccordionItem
                    key={month}
                    value={month}
                    className="border-none mx-4 mr-0 mt-1"
                  >
                    <AccordionTrigger className="flex items-center py-0 mb-3">
                      <MonthTitle>{month}</MonthTitle>
                      <Separator className="flex-1" />
                      <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                    </AccordionTrigger>
                    <AccordionContent>
                      {[...sessionsForMonth]
                        .sort((a, b) => b.createdAt - a.createdAt)
                        .map((event) => renderEvent(event))}
                    </AccordionContent>
                  </AccordionItem>
                ))}
            </div>
          ))
      ) : (
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-6">
          <PulseIcon size={80} className="text-neutral-400" weight="thin" />
          <div className="flex flex-col items-center gap-2">
            <h3 className="text-lg font-medium text-neutral-900">
              No activity yet
            </h3>
            <p className="text-sm text-neutral-500 max-w-[400px] text-center">
              This learner hasn't completed any activities in Spellcaster yet.
              Activity will appear here once they start playing.
            </p>
          </div>
        </div>
      )}
    </Accordion>
  );
}

const YearTitle = styled.span`
  font-size: 16px;
  font-weight: 500;
`;

const MonthTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
`;
